import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Paper, Box, Divider, List, ListItem, Typography, } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getAvgRating, groupdByRatings } from '../../../common/rating';
import CustomButton from '../../../common/CustomButton';
import PanelTitle from '../PanelTitle';
import RatingPercentLine from './RatingPercentLine';
import ReviewItem from './ReviewItem';
import TotalRating from './TotalRating';
const StyledDivider = styled(Divider)(({ theme }) => ({
    borderColor: theme.portal.site.lineColor,
    borderBottomWidth: 1,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
}));
const ReviewsContent = ({ reviews, config: { showTitle, showStats, showStatsAvg, showStatsAvgRating, showStatsAvgRatingStars, showStatsAvgCount, showStatsAvgBars, showReviews, showReviewsTitle, showReviewerName, showReviewDate, showReviewStars, reviewsCount, }, }) => {
    const [showAllReviews, setShowAllReviews] = React.useState(false);
    const totalAvgRating = getAvgRating(reviews);
    const totalReviews = (reviews && reviews.length) || 0;
    const reviewsByRating = groupdByRatings(reviews);
    const reviewsHeadRef = React.useRef(null);
    const showOrHideReviews = () => {
        setShowAllReviews(!showAllReviews);
        if (showAllReviews) {
            // @ts-ignore
            reviewsHeadRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
    };
    return (_jsxs(Paper, { elevation: 0, sx: { display: 'flex', flexDirection: 'column' }, ref: reviewsHeadRef, children: [showTitle && _jsx(PanelTitle, { title: "REVIEWS" }), (showStats || showReviews) && (_jsxs(Box, { pt: 2.5, sx: {
                    display: 'flex',
                    flexDirection: 'row',
                    flex: 1,
                }, children: [showStats && (_jsxs(Box, { sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 0.3,
                            minWidth: 250,
                        }, children: [showStatsAvg
                                && (_jsx(TotalRating, { avgRating: totalAvgRating, reviewsCount: totalReviews, showStatsAvgRating: showStatsAvgRating, showStatsAvgRatingStars: showStatsAvgRatingStars, showStatsAvgCount: showStatsAvgCount })), showStatsAvgBars && (_jsx(Box, { ml: 7.5, mt: 4.5, children: Object.keys(reviewsByRating).reverse().map((key) => (_jsx(RatingPercentLine, { total: reviews.length, current: reviewsByRating[key] }, key))) }))] })), showReviews && (_jsxs(Box, { sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 0.7,
                            marginLeft: 8,
                        }, children: [showReviewsTitle && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "subtitle1", fontWeight: 600, sx: { textTransform: 'capitalize' }, children: "top reviews from introduction to data analytics" }), _jsx(StyledDivider, {})] })), _jsxs(List, { children: [reviews.slice(0, reviewsCount).map((r, index) => (_jsx(Box, { children: showAllReviews
                                            ? (_jsx(ListItem, { sx: { pl: 0 }, children: _jsx(ReviewItem, { review: r, showName: showReviewerName, showDate: showReviewDate, showStars: showReviewStars }) })) : (index <= 3 && (_jsx(ListItem, { sx: { pl: 0 }, children: _jsx(ReviewItem, { review: r, showName: showReviewerName, showDate: showReviewDate, showStars: showReviewStars }) }))) }, r.id))), (reviews.length > 4)
                                        && (_jsx(ListItem, { sx: { pl: 0 }, children: _jsx(CustomButton, { onClick: showOrHideReviews, label: showAllReviews ? 'Minimize reviews' : 'View all reviews', customStyle: { width: 159, height: 34 } }) }, "showAll"))] })] }))] }))] }));
};
ReviewsContent.defaultProps = {
    settings: {
        showTitle: true,
        showStats: true,
        showStatsAvg: true,
        showStatsAvgRating: true,
        showStatsAvgRatingStars: true,
        showStatsAvgCount: true,
        showStatsAvgBars: true,
        showReviews: true,
        showReviewsTitle: true,
        showReviewerName: true,
        showReviewDate: true,
        showReviewStars: true,
        reviewsCount: undefined, // show all
    },
};
ReviewsContent.meta = {
    group: 'hidden',
    name: 'Course Reviews Tab',
    description: 'Component displays the course reviews list',
    props: [
        {
            key: 'course',
            name: 'Course',
            type: 'course', // note this is a special type recognized by the system
            description: 'The course to display information about',
        },
        {
            key: 'settings.showTitle',
            name: 'Show Title',
            type: 'boolean',
            description: 'Show the title on the Reviews panel',
        },
        {
            key: 'settings.showStats',
            name: 'Show Statistics',
            type: 'boolean',
            description: 'Show statistics of reviews',
        },
        {
            key: 'settings.showStatsAvg',
            name: 'Show avg rating all',
            type: 'boolean',
            description: 'Show overall stats',
        },
        {
            key: 'settings.showStatsAvgRating',
            name: 'Show avg rating',
            type: 'boolean',
            description: 'Show overall rating',
        },
        {
            key: 'settings.showStatsAvgRatingStars',
            name: 'Show avg rating stars',
            type: 'boolean',
            description: 'Show overall rating in stars',
        },
        {
            key: 'settings.showStatsAvgCount',
            name: 'Show avg rating count',
            type: 'boolean',
            description: 'Show overall rating count',
        },
        {
            key: 'settings.showStatsAvgBars',
            name: 'Show rating bars',
            type: 'boolean',
            description: 'Show the statictic bar by rating',
        },
        {
            key: 'settings.showReviews',
            name: 'Show Reviews',
            type: 'boolean',
            description: 'Show the reviews list',
        },
        {
            key: 'settings.showReviewsTitle',
            name: 'Show Reviews Title',
            type: 'boolean',
            description: 'Show the reviews title',
        },
        {
            key: 'settings.showReviewerName',
            name: 'Show Reviewer Name',
            type: 'boolean',
            description: 'Show the first and last name of user.',
        },
        {
            key: 'settings.showReviewDate',
            name: 'Show Review Date',
            type: 'boolean',
            description: 'Show review date.',
        },
        {
            key: 'settings.showReviewStars',
            name: 'Show Review Stars',
            type: 'boolean',
            description: 'Show star rating .',
        },
        {
            key: 'settings.reviewsCount',
            name: 'Reviews count',
            type: 'number',
            description: 'Display limit for reviews list.',
        },
    ],
    defaultConfig: {
        group: 'hidden',
        type: 'component',
        component: 'Reviews',
        props: {
            reviews: '@course.reviews',
            settings: {
                showTitle: true,
                showStats: true,
                showStatsAvg: true,
                showStatsAvgRating: true,
                showStatsAvgRatingStars: true,
                showStatsAvgCount: true,
                showStatsAvgBars: true,
                showReviews: true,
                showReviewsTitle: true,
                showReviewerName: true,
                showReviewDate: true,
                showReviewStars: true,
                reviewsCount: undefined, // show all
            },
        },
    },
};
export default observer(ReviewsContent);
//# sourceMappingURL=ReviewsContent.js.map